import type { CardDetail, CardPricePoint } from "@hobbyshopify/backend-client";
import { Rx } from "subtree.js/core";
import {
  type CardEditing,
  type CardFields,
  type CardSection,
  cardBase,
  changeLine,
  countDropped,
  type StockLossKind,
} from "../../core/primitives/card-edit";
import { idleLoad, type LoadStatus } from "../../core/primitives/load-status";

/** The card as read, and what the merchant typed over it. */
export function cardEditing(
  detail: CardDetail,
  fields: CardFields,
  lossKind: StockLossKind,
  lossNote: string,
): CardEditing {
  return { card: detail.card, base: cardBase(detail), fields, lossKind, lossNote };
}

/**
 * What the Save bar says Save will do, and whether it asks why the count went down. While a Save goes, a lower count
 * is a loss only against the card the page showed when Save was pressed (`savingFrom`), as the Save sends it.
 */
export function cardSaveBar(editing: CardEditing, savingFrom: CardFields | null): { line: string; asksWhy: boolean } {
  const shown = savingFrom ?? editing.base;
  return {
    line: changeLine(editing, shown),
    asksWhy: countDropped(editing.fields, editing.base, editing.card) && editing.fields.onHand < shown.onHand,
  };
}

export class CardDetailState {
  readonly detail = new Rx<CardDetail | null>(null);
  readonly status = new Rx<LoadStatus>(idleLoad);
  /** What a customer was quoted, when the card was opened from their scan. */
  readonly quoteNote = new Rx<string | null>(null);
  /** The section a Stock row's menu asked for, once the card and its prices are read; null once scrolled to. */
  readonly scrollTo = new Rx<CardSection | null>(null);
  /** Every price recorded for the card, oldest first. */
  readonly history = new Rx<readonly CardPricePoint[]>([]);
  /** What the merchant has typed; a draft until Save. Null until the card is read. */
  readonly fields = new Rx<CardFields | null>(null);
  /** Asked only when the count goes down: losses are totalled apart from corrections. */
  readonly lossKind = new Rx<StockLossKind>("SHRINKAGE");
  readonly lossNote = new Rx("");
  readonly saving = new Rx(false);
  /** The card as the page showed it when Save was pressed, while that Save goes: a lower count is a loss only
   * against it, as the Save sends it. After a Save whose card was not read again, the count it sent, until a read. */
  readonly savingFrom = new Rx<CardFields | null>(null);
  readonly repricing = new Rx(false);
  readonly archiving = new Rx(false);
  /** "Archive this card?" is open. */
  readonly confirmingArchive = new Rx(false);
  /** A refusal or a failure, until the next action. */
  readonly error = new Rx<string | null>(null);
  /** An archive's refusal or failure, said beside Archive card, where the focus comes back to. */
  readonly archiveError = new Rx<string | null>(null);
  /** A result rather than a failure: "Repriced to $6.00.", "Saved. …". */
  readonly notice = new Rx<string | null>(null);
  /** A write another page left on the card was still on its way when the page read it: the page reads it again once it lands. */
  readonly waiting = new Rx(false);
  /** That write landed, and every read asked after it failed: the page still shows the card as it was before it. */
  readonly landedUnread = new Rx(false);
  /** Whether the caller may change the card. `true` until the role is known. */
  readonly canEditInventory = new Rx(true);
}

export abstract class CardDetailActions {
  abstract setFields(patch: Partial<CardFields>): void;
  /** On with a blank floor starts it at the asking price. */
  abstract setSeparateFloor(on: boolean): void;
  abstract setLossKind(kind: StockLossKind): void;
  abstract setLossNote(note: string): void;
  /** Back to the card's own values. */
  abstract discard(): void;
  abstract save(): Promise<void>;
  abstract refreshPrice(): Promise<void>;
  abstract askArchive(): void;
  abstract cancelArchive(): void;
  abstract archive(): Promise<void>;
  abstract retry(): void;
  /** Back to where the card was opened from. */
  abstract back(): void;
  /** The page brought the asked-for section into view. */
  abstract scrolled(): void;
}
